import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { Link } from "react-router-dom";
import { Button } from "../Button";
import { Loader } from "../Loader";
import { ROUTES } from "../../config/routes";
import { UsersAdminService } from "../../api/generated/services/UsersAdminService";
import { ErasureDetailsViewModel } from "../../api/generated/models/ErasureDetailsViewModel";
import { ErasureStepStatus } from "../../api/generated/models/ErasureStepStatus";
import { ReactComponent as CloseIcon } from "../../assets/icons/x.svg";
import { formatCopenhagenDateTime } from "../../helpers/dateFilters";
import "../../api/openApiConfig";

const stepStatusClass = (status?: ErasureStepStatus) => {
  switch (status) {
    case ErasureStepStatus.COMPLETED:
      return "bg-green-100 text-green-800";
    case ErasureStepStatus.SKIPPED:
      return "bg-gray-100 text-gray-800";
    case ErasureStepStatus.FAILED:
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

interface Props {
  isOpen: boolean;
  identityId: string | null;
  onClose: () => void;
}

const ErasureDetailsModal: React.FC<Props> = ({ isOpen, identityId, onClose }) => {
  const [details, setDetails] = useState<ErasureDetailsViewModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !identityId) return;
    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        setDetails(null);
        const response = await UsersAdminService.postApiUsersAdminGetErasure({ identityId });
        if (ignore) return;
        setDetails(response);
        setError(null);
      } catch (err) {
        if (ignore) return;
        setError("Failed to fetch the erasure record");
        console.error("Error fetching erasure details:", err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
  }, [isOpen, identityId]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal-content max-w-[900px]"
      overlayClassName="modal-overlay"
    >
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="font-medium">Erasure record</h3>
          <div className="font-mono text-xs text-gray-500 break-all">{identityId}</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>
      </div>

      {loading && (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      )}

      {error && <div className="error mb-4">{error}</div>}

      {details && (
        <>
          <div className="flex items-center gap-4 mb-4">
            <div>
              <div className="text-sm text-gray-500">Status</div>
              <div>{details.status}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Completed</div>
              <div>{formatCopenhagenDateTime(details.erasureCompletedAt as any)}</div>
            </div>
            {identityId && (
              <Link
                className="ml-auto"
                to={`${ROUTES.ADMIN_USERS()}?search=${identityId}`}
                onClick={onClose}
              >
                <Button variant="outline" size="sm" type="button">
                  Open account
                </Button>
              </Link>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th className="whitespace-nowrap">Completed</th>
                  <th>Detail</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {(details.steps ?? []).map((step) => (
                  <tr key={step.step}>
                    <td className="whitespace-nowrap">{step.step}</td>
                    <td>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${stepStatusClass(
                          step.status
                        )}`}
                      >
                        {step.status}
                      </span>
                    </td>
                    <td>{step.attempts ?? 0}</td>
                    <td className="whitespace-nowrap">
                      {formatCopenhagenDateTime(step.completedAt as any)}
                    </td>
                    {/* PII-free by contract on the backend: counts and technical ids only. */}
                    <td className="font-mono text-xs break-all">{step.detail || "-"}</td>
                    <td className="text-xs break-all text-red-700">{step.error || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(details.steps ?? []).length === 0 && (
            <div className="text-gray-500 mt-4">
              Nothing has run for this account yet.
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default ErasureDetailsModal;
